<script setup lang="ts">
import { FormInput, FormLabel } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import axios from "@/utils/axios";
import Toastify from "toastify-js";

const { t } = useI18n();
const router = useRouter();
const email = ref("");
const isLoading = ref(false);
const isSuccess = ref(false);

const handleRecover = async () => {
    isLoading.value = true;
    try {
        await axios.post("/auth/forgot-password", {
            email: email.value
        });
        isSuccess.value = true;
        Toastify({
            text: t("auth.instructions_sent_msg"),
            style: { background: "linear-gradient(to right, #00b09b, #96c93d)" }
        }).showToast();
    } catch (error: any) {
        Toastify({
            text: error.response?.data?.message || t("common.error"),
            style: { background: "#ef4444" }
        }).showToast();
    } finally {
        isLoading.value = false;
    }
}
</script>

<template>
  <div
    class="container grid lg:h-screen grid-cols-12 lg:max-w-[1550px] 2xl:max-w-[1750px] py-10 px-5 sm:py-14 sm:px-10 md:px-36 lg:py-0 lg:pl-14 lg:pr-12 xl:px-24"
  >
    <div
      :class="[
        'relative z-50 h-full col-span-12 p-7 sm:p-14 bg-white rounded-2xl lg:bg-transparent lg:pr-10 lg:col-span-5 xl:pr-24 2xl:col-span-4 lg:p-0 dark:bg-darkmode-600',
        'before:content-[\'\'] before:absolute before:inset-0 before:-mb-3.5 before:bg-white/40 before:rounded-2xl before:mx-5 dark:before:hidden',
      ]"
    >
      <div
        class="relative z-10 flex flex-col justify-center w-full h-full py-2 lg:py-32"
      >
        <div class="flex items-center justify-center">
          <img alt="Logo" class="w-16" src="@/assets/images/logo.svg" />
        </div>
        <div class="mt-10">
          <div class="text-2xl font-medium">{{ $t('auth.recover_password') }}</div>
          <div class="mt-2.5 text-slate-800 dark:text-slate-600">
            {{ $t('auth.forgot_pass_subtitle') }}
          </div>
          
          <div v-if="isSuccess" class="mt-6 p-4 text-green-700 bg-green-100 rounded-lg">
            {{ $t('auth.instructions_sent_msg') }}
            <div class="mt-4">
              <router-link to="/login" class="font-medium text-primary underline">{{ $t('auth.back_to_login') }}</router-link>
            </div>
          </div>

          <form v-else @submit.prevent="handleRecover" class="mt-6">
            <FormLabel>{{ $t('auth.email') }}</FormLabel>
            <FormInput
              type="email"
              class="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
              :placeholder="$t('auth.email')"
              v-model="email"
              required
            />
            
            <div class="mt-5 text-center xl:mt-8 xl:text-left flex flex-col gap-3">
              <Button
                variant="primary"
                rounded
                type="submit"
                class="bg-gradient-to-r from-theme-1/70 to-theme-2/70 w-full py-3.5 xl:mr-3 dark:border-darkmode-400"
                :disabled="isLoading || !email"
              >
                <svg v-if="isLoading" class="w-5 h-5 mr-3 -ml-1 text-white animate-spin inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isLoading ? $t('auth.loading') : $t('auth.send_instructions') }}
              </Button>
              <router-link class="font-medium text-primary text-center mt-2 underline" to="/login"> {{ $t('auth.back_to_login') }} </router-link>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <div
    class="fixed container grid w-screen inset-0 h-screen grid-cols-12 lg:max-w-[1550px] 2xl:max-w-[1750px] pl-14 pr-12 xl:px-24"
  >
    <div
      :class="[
        'relative h-screen col-span-12 lg:col-span-5 2xl:col-span-4 z-20',
        'after:bg-white after:hidden after:lg:block after:content-[\'\'] after:absolute after:right-0 after:inset-y-0 after:bg-gradient-to-b after:from-white after:to-slate-100/80 after:w-[800%] after:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] dark:after:bg-darkmode-600 dark:after:from-darkmode-600 dark:after:to-darkmode-600',
        'before:content-[\'\'] before:hidden before:lg:block before:absolute before:right-0 before:inset-y-0 before:my-6 before:bg-gradient-to-b before:from-white/10 before:to-slate-50/10 before:bg-white/50 before:w-[800%] before:-mr-4 before:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] dark:before:from-darkmode-300 dark:before:to-darkmode-300',
      ]"
    ></div>
    <div
      :class="[
        'h-full col-span-7 2xl:col-span-8 lg:relative',
        'before:content-[\'\'] before:absolute before:lg:-ml-10 before:left-0 before:inset-y-0 before:bg-gradient-to-b before:from-theme-1 before:to-theme-2 before:w-screen before:lg:w-[800%]',
        'after:content-[\'\'] after:absolute after:inset-y-0 after:left-0 after:w-screen after:lg:w-[800%] after:bg-texture-white after:bg-fixed after:bg-center after:lg:bg-[25rem_-25rem] after:bg-no-repeat',
      ]"
    >
      <div
        class="sticky top-0 z-10 flex-col justify-center hidden h-screen ml-16 lg:flex xl:ml-28 2xl:ml-36"
      >
        <div
          class="leading-[1.4] text-[2.6rem] xl:text-5xl font-medium xl:leading-[1.2] text-white"
        >
           {{ $t('auth.access_recovery_title') }}
        </div>
        <div class="mt-5 text-base leading-relaxed xl:text-lg text-white/70">
          {{ $t('auth.access_recovery_desc') }}
        </div>
      </div>
    </div>
  </div>
</template>
